import {
  DAYS_PER_MONTH,
  DAYS_PER_YEAR,
  HOURS_PER_DAY,
  MINUTES_PER_HOUR,
  MONTHS_PER_YEAR,
  SECONDS_PER_MINUTE,
} from "./time.constants";
import type { TimeUnits } from "./time.types";

/**
 * Calculate the time units from a given number of minutes
 * @param minutes - Number of minutes to calculate the time units from
 * @returns Time units object
 */
export function calculateTimeUnits(
  minutes?: string | number
): TimeUnits | undefined {
  if (!minutes || Number.isNaN(Number(minutes)) || +minutes === 0) return undefined;

  const totalSeconds = parseInt(String(minutes), 10) * SECONDS_PER_MINUTE;

  // Calculate from largest to smallest unit with proper remainders
  const totalMinutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const totalHours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const totalDays = Math.floor(totalHours / HOURS_PER_DAY);

  // Years are derived from a real calendar year (365 days) rather than by
  // chaining through 12 x 30-day months (a 360-day year). Chaining through
  // months would drift 5 days/year against actual 365-day-based presets
  // (e.g. `TIME_WINDOWS_MAP.YEAR`), surfacing as a bogus extra month once the
  // accumulated drift crosses the day-to-month rounding threshold (3+ years).
  const yearsFromDays = Math.floor(totalDays / DAYS_PER_YEAR);
  const daysAfterYears = totalDays - yearsFromDays * DAYS_PER_YEAR;

  const monthsFromRemainder = Math.floor(daysAfterYears / DAYS_PER_MONTH);
  const days = daysAfterYears % DAYS_PER_MONTH;

  // A remainder of 12 whole months (360-364 days) is effectively another
  // year even though it falls short of a full 365-day year.
  const years = yearsFromDays + Math.floor(monthsFromRemainder / MONTHS_PER_YEAR);
  const months = monthsFromRemainder % MONTHS_PER_YEAR;

  const hours = totalHours % HOURS_PER_DAY;
  const mins = totalMinutes % MINUTES_PER_HOUR;
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  return {
    years,
    months,
    days,
    hours,
    minutes: mins,
    seconds,
  };
}
